// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FolderOpen, Trash2, Edit, BookOpen, Calendar, User } from 'lucide-react';
import ApiService from '../services/api';
import usePiegeFocus from '../hooks/usePiegeFocus';
import notificationService from '../utils/notificationService';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // FIX-009
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  // Dialogues : focus piégé, Échap ferme, focus rendu au bouton d'ouverture.
  const fermerCreation = () => setShowCreateModal(false);
  const fermerEdition = () => setShowEditModal(false);
  const fermerSuppression = () => setShowDeleteModal(false);
  const refCreation = usePiegeFocus(showCreateModal, fermerCreation);
  const refEdition = usePiegeFocus(showEditModal && Boolean(projectToEdit), fermerEdition);
  const refSuppression = usePiegeFocus(showDeleteModal && Boolean(projectToDelete), fermerSuppression);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getProjects();
      setProjects(data.projects || data || []);
    } catch (err) {
      setError(err?.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      notificationService.showToast('Le nom du projet est requis', 'warning');
      return;
    }
    try {
      setCreating(true);
      const data = await ApiService.createProject(newProject);
      setProjects([(data.data || data), ...projects]);
      setNewProject({ name: '', description: '' });
      setShowCreateModal(false);
    } catch (err) {
      notificationService.showToast(err?.message || 'Erreur création', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleEditProject = (proj) => {
    setProjectToEdit(proj);
    setEditForm({ name: proj.name || '', description: proj.description || '' });
    setShowEditModal(true);
  };

  const confirmEditProject = async () => {
    if (!projectToEdit) return;
    try {
      await ApiService.updateProject(projectToEdit.id, editForm);
      setProjects(projects.map(p => p.id === projectToEdit.id ? { ...p, ...editForm } : p));
      setShowEditModal(false);
      setProjectToEdit(null);
    } catch (err) {
      notificationService.showToast(err?.message || 'Erreur mise à jour', 'error');
    }
  };

  const handleDeleteProject = (projectId, projectName) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setShowDeleteModal(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      await ApiService.deleteProject(projectToDelete.id);
      setProjects(projects.filter(p => p.id !== projectToDelete.id));
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (err) {
      notificationService.showToast(err?.message || 'Erreur suppression', 'error');
    }
  };

  // Formater la date
  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="projects-container app-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement des projets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="projects-container app-page">
        <div className="error-message">
          <h3>Erreur</h3>
          <p>{error}</p>
          <button onClick={loadProjects}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container app-page">
      <div className="projects-header">
        <div className="projects-title">
          <FolderOpen size={32} />
          <h1>Projets</h1>
        </div>
        <p className="projects-subtitle">
          Créez des espaces de travail pour organiser vos livres et converser avec l'IA
        </p>
        <button
          className="create-project-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={20} />
          Nouveau Projet
        </button>
      </div>

      <div className="projects-grid">
        {projects.length === 0 ? (
          <div className="empty-state">
            <FolderOpen size={64} />
            <h3>Aucun projet</h3>
            <p>Créez votre premier projet pour commencer à organiser vos livres</p>
            <button
              className="create-project-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={20} />
              Créer un projet
            </button>
          </div>
        ) : (
          projects.map(project => (
            <div key={project.id} className="project-card">
              <div className="project-card-header">
                <div className="project-info">
                  <h3>{project.name}</h3>
                  {project.description && (
                    <p className="project-description">{project.description}</p>
                  )}
                </div>
                <div className="project-actions">
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditProject(project)}
                    title="Modifier le projet"
                    aria-label={`Modifier le projet ${project.name}`}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteProject(project.id, project.name)}
                    title="Supprimer le projet"
                    aria-label={`Supprimer le projet ${project.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="project-stats">
                <div className="stat">
                  <BookOpen size={16} />
                  <span>{project.book_count} livre{project.book_count !== 1 ? 's' : ''}</span>
                </div>
                <div className="stat">
                  <Calendar size={16} />
                  <span>{formatDate(project.created_at)}</span>
                </div>
                <div className="stat">
                  <User size={16} />
                  <span>{project.user_id}</span>
                </div>
              </div>

              <div className="project-card-footer">
                <Link
                  to={`/projects/${project.id}`}
                  className="open-project-btn"
                >
                  <FolderOpen size={16} />
                  Ouvrir le projet
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de création de projet */}
      {showCreateModal && (
        // Le clic sur le fond reste un raccourci souris ; au clavier, Échap
        // ferme (usePiegeFocus). Le fond n'est donc pas un faux bouton.
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerCreation(); }}>
          <div
            className="modal-content"
            ref={refCreation}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-creation-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-creation-titre">Créer un nouveau projet</h2>
              <button
                type="button"
                className="modal-close"
                onClick={fermerCreation}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label htmlFor="project-name">Nom du projet *</label>
                <input
                  id="project-name"
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  placeholder="Ex: Recherche sur l'IA"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="project-description">Description (optionnel)</label>
                <textarea
                  id="project-description"
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  placeholder="Décrivez l'objectif de ce projet..."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creating || !newProject.name.trim()}
                >
                  {creating ? 'Création...' : 'Créer le projet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition (FIX-009) */}
      {showEditModal && projectToEdit && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerEdition(); }}>
          <div
            className="modal-content"
            ref={refEdition}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-edition-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-edition-titre">Modifier le projet</h2>
              <button type="button" className="modal-close" onClick={fermerEdition} aria-label="Fermer">×</button>
            </div>
            <div className="form-group">
              <label htmlFor="projet-edition-nom">Nom *</label>
              <input id="projet-edition-nom" type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label htmlFor="projet-edition-description">Description</label>
              <textarea id="projet-edition-description" value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} rows={3} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Annuler</button>
              <button type="button" className="btn-primary" onClick={confirmEditProject} disabled={!editForm.name.trim()}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && projectToDelete && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerSuppression(); }}>
          <div
            className="modal-content"
            ref={refSuppression}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-suppression-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-suppression-titre">Confirmer la suppression</h2>
              <button
                type="button"
                className="modal-close"
                onClick={fermerSuppression}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer le projet <strong>"{projectToDelete.name}"</strong> ?</p>
              <p>Cette action est irréversible.</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDeleteProject}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
